import "bootstrap-icons/font/bootstrap-icons.css";
import { Link, Navigate, useNavigate } from "react-router-dom";
import "./purchase.css";

import { Button, DatePicker, Dropdown, Menu, Segmented, Table } from "antd";
import { MoreOutlined, EditOutlined, DollarOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { loadAllPurchase } from "../../redux/actions/purchase/getPurchaseAction";
import { deletePurchase } from "../../redux/actions/purchase/deletePurchaseAction";
import DashboardCard from "../Card/DashboardCard";
import PurchaseInvoice from "../Invoice/PurchaseInvoice";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../utils/dateFilterCache";

/** PDF "Total Gross": before line-item % discount. */
function purchaseGrossTotal(record) {
	const ta = Number(record.total_amount) || 0;
	const ro = Number(record.round_off_amount) || 0;
	const tpd = Number(record.total_product_discount) || 0;
	return ta - ro + tpd;
}

/** PDF "Grand Total" after additional discount & round-off (same formula as PurchaseInvoice PDF). */
function purchaseBillGrandTotal(record) {
	const ta = Number(record.total_amount) || 0;
	const ro = Number(record.round_off_amount) || 0;
	const addDisc = Number(record.bill_discount_amount ?? record.discount) || 0;
	const subAfterLine = ta - ro;
	return subAfterLine - addDisc - Math.abs(ro);
}

function fmtMoney2(n) {
	const x = Number(n);
	if (!Number.isFinite(x)) return "0.00";
	return x.toFixed(2);
}

function CustomTable({ list, total, status, setStatus, startdate, enddate, paginationConfig, onShowPdf, onPrintPdf }) {
	const [columnItems, setColumnItems] = useState([]);
	const [columnsToShow, setColumnsToShow] = useState([]);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const { current: currentPage, pageSize, onChange: onPaginationChange } = paginationConfig || {};

	const handleConvertToSale = () => {
		if (!selectedRows.length) {
			return;
		}
		const ids = selectedRows.map((row) => row.id).join(",");
		if (!ids) {
			return;
		}
		const shouldProceed = window.confirm(
			"Convert selected purchases into a new sale invoice?"
		);
		if (!shouldProceed) {
			return;
		}
		navigate(`/sale?sourceType=purchase&sourceIds=${ids}`);
	};
	const onChange = (value) => {
		setStatus(value);
		dispatch(
			loadAllPurchase({
				status: value,
				page: 1,
				limit: pageSize || 10,
				startdate,
				enddate,
			})
		);
		onPaginationChange && onPaginationChange(1, pageSize || 10);
	};

	const handleDelete = async (id) => {
		if (!window.confirm("Are you sure you want to delete this purchase invoice?")) {
			return;
		}
		const result = await dispatch(deletePurchase(id));
		if (result?.message === "success") {
			dispatch(
				loadAllPurchase({
					page: currentPage || 1,
					limit: pageSize || 10,
					startdate,
					enddate,
				})
			);
		} else if (result?.error) {
			console.error("Delete purchase failed:", result.error);
		}
	};

	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			defaultSortOrder: "descend",
			sorter: (a, b) => new Date(a.date) - new Date(b.date),
			render: (date) => moment(date).format("ll"),
		},
		{
			title: "Supplier Name ",
			dataIndex: `supplier`,
			key: "supplier_id",
			render: (supplier, record) => (
				<Link to={`/purchase/${record.id}`}>
					{supplier?.name}
				</Link>
			),
		},
		{
			title: "Total Amount",
			dataIndex: "total_amount",
			key: "total_amount",
			render: (value) => fmtMoney2(value ?? 0),
		},
		{
			title: "Discount",
			dataIndex: "total_product_discount",
			key: "total_product_discount",
			render: (value) => fmtMoney2(value ?? 0),
		},
		{
			title: "Add. Discount",
			dataIndex: "bill_discount_amount",
			key: "bill_discount_amount",
			render: (_, record) =>
				fmtMoney2(
					record.bill_discount_amount != null && record.bill_discount_amount !== ""
						? record.bill_discount_amount
						: Number(record.discount) || 0
				),
		},
		{
			title: "Bill Amt.",
			key: "bill_amount_rounded",
			render: (_, record) => {
				const g = purchaseBillGrandTotal(record);
				return `₹${Math.round(g)}`;
			},
		},
		{
			title: "Paid Amt.",
			dataIndex: "paid_amount",
			key: "paid_amount",
			render: (value) => fmtMoney2(value ?? 0),
		},
		{
			title: "Pending Amt.",
			dataIndex: "due_amount",
			key: "due_amount",
			render: (value) => fmtMoney2(value ?? 0),
		},

		//Update Supplier Name here

		{
			title: "Action",
			dataIndex: "id",
			key: "action",
			render: (id, record) => {
				const menu = (
					<Menu>
						<Menu.Item
							key="payment"
							icon={<DollarOutlined />}
							onClick={() => navigate(`/payment/supplier/${id}`)}
						>
							Payment
						</Menu.Item>
						{onShowPdf && (
							<Menu.Item
								key="preview"
								onClick={() => onShowPdf(record)}
							>
								Preview
							</Menu.Item>
						)}
						{onPrintPdf && (
							<Menu.Item
								key="print"
								onClick={() => onPrintPdf(record)}
							>
								Print PDF
							</Menu.Item>
						)}
						<Menu.Item
							key="edit"
							icon={<EditOutlined />}
							onClick={() => navigate(`/purchase/edit/${id}`)}
						>
							Edit
						</Menu.Item>
						<Menu.Item
							key="delete"
							icon={<DeleteOutlined />}
							danger
							onClick={() => handleDelete(id)}
						>
							Delete
						</Menu.Item>
					</Menu>
				);
				return (
					<Dropdown overlay={menu} trigger={["click"]}>
						<Button type="text" size="small" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
					</Dropdown>
				);
			},
		},
	];

	useEffect(() => {
		setColumnItems(menuItems);
		setColumnsToShow(columns);
	}, []);

	const colVisibilityClickHandler = (col) => {
		const ifColFound = columnsToShow.find((item) => item.key === col.key);
		if (ifColFound) {
			const filteredColumnsToShow = columnsToShow.filter(
				(item) => item.key !== col.key
			);
			setColumnsToShow(filteredColumnsToShow);
		} else {
			const foundIndex = columns.findIndex((item) => item.key === col.key);
			const foundCol = columns.find((item) => item.key === col.key);
			let updatedColumnsToShow = [...columnsToShow];
			updatedColumnsToShow.splice(foundIndex, 0, foundCol);
			setColumnsToShow(updatedColumnsToShow);
		}
	};

	const menuItems = columns.map((item) => {
		return {
			key: item.key,
			label: <span>{item.title}</span>,
		};
	});

	const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

	const CSVlist = list?.map((i) => ({
		...i,
		supplier: i?.supplier?.name,
		total_amount_gross: purchaseGrossTotal(i),
		line_discount: Number(i.total_product_discount) || 0,
		additional_discount:
			i.bill_discount_amount != null ? Number(i.bill_discount_amount) : Number(i.discount) || 0,
		bill_amount_rounded: Math.round(purchaseBillGrandTotal(i)),
	}));

	const rowSelection = {
		selectedRowKeys,
		onChange: (keys, rows) => {
			setSelectedRowKeys(keys);
			setSelectedRows(rows);
		},
		preserveSelectedRowKeys: true,
	};

	return (
		<div>
			{list && (
				<div className='text-center d-flex flex-wrap justify-content-between align-items-center gap-2 list-page-header mb-2' style={{ marginBottom: "8px" }}>
					<div className='d-flex flex-wrap align-items-center gap-2'>
						<Button
							type='primary'
							onClick={handleConvertToSale}
							disabled={!selectedRowKeys.length}>
							Convert to Sale
							{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ""}
						</Button>
						<CSVLink
							data={CSVlist}
							className='btn btn-dark btn-sm'
							filename='purchase'>
							Download CSV
						</CSVLink>
						<Dropdown
							overlay={
								<Menu onClick={colVisibilityClickHandler} items={columnItems} />
							}
							placement='bottomLeft'>
							<Button>Column Visibility</Button>
						</Dropdown>
					</div>
					<Segmented
						className='text-center rounded danger'
						size='middle'
						options={[
							{
								label: (
									<span>
										<i className='bi bi-person-lines-fill'></i> Active
									</span>
								),
								value: "true",
							},
							{
								label: (
									<span>
										<i className='bi bi-person-dash-fill'></i> Inactive
									</span>
								),
								value: "false",
							},
						]}
						value={status}
						onChange={onChange}
					/>
				</div>
			)}
			{/* Desktop: table */}
			<div className="d-none d-lg-block list-page-table-container purchase-list-table-container" style={{ minHeight: 520 }}>
				<div className="list-page-table-inner">
					<div className="list-page-table-wrap purchase-list-table-wrap">
						<Table
							scroll={{ x: true, y: 300 }}
							loading={!list}
							pagination={{
								current: currentPage || 1,
								pageSize: pageSize || 10,
								total: total ?? 0,
								pageSizeOptions: [10, 20, 50, 100, 200],
								showSizeChanger: true,
								hideOnSinglePage: false,
								showTotal: (tot, range) => {
									const t = total ?? 0;
									if (!t) return "0 items";
									return `${range[0]}-${range[1]} of ${t} items`;
								},
								onChange: (page, limit) => {
									onPaginationChange && onPaginationChange(page, limit);
									dispatch(
										loadAllPurchase({ status, page, limit, startdate, enddate })
									);
								},
							}}
							rowSelection={rowSelection}
							columns={columnsToShow}
							dataSource={list ? addKeys(list) : []}
						/>
					</div>
				</div>
			</div>

			{/* Mobile: card layout – scrollable list */}
			<div className="d-lg-none purchase-list-mobile-cards">
				{list && list.length > 0 ? (
					<div className="row">
						{list.map((record) => (
							<div key={record.id} className="col-12 mb-3">
								<div className="card">
									<div className="card-body">
										<div className="d-flex justify-content-between align-items-start">
											<h6 className="card-title mb-0">
												<Link to={`/purchase/${record.id}`} style={{ textDecoration: "none", color: "#1890ff" }}>
													{record.supplier?.name || "Purchase"}
												</Link>
											</h6>
											<small className="text-muted">{moment(record.date).format("MMM DD, YYYY")}</small>
										</div>
										<div className="mb-2">
											<small className="text-muted">
												<strong>Supplier:</strong> {record.supplier?.name || "N/A"}
											</small>
										</div>
										<div className="row mb-2">
											<div className="col-6">
												<small className="text-muted">
													<strong>Total:</strong> ₹{fmtMoney2(record.total_amount ?? 0)}
												</small>
											</div>
											<div className="col-6">
												<small className="text-muted">
													<strong>Discount:</strong> ₹{fmtMoney2(record.total_product_discount ?? 0)}
												</small>
											</div>
										</div>
										<div className="row mb-2">
											<div className="col-6">
												<small className="text-muted">
													<strong>Add. disc.:</strong> ₹{fmtMoney2(
														record.bill_discount_amount != null
															? record.bill_discount_amount
															: Number(record.discount) || 0
													)}
												</small>
											</div>
											<div className="col-6">
												<small className="text-muted">
													<strong>Bill (rounded):</strong> ₹{Math.round(purchaseBillGrandTotal(record))}
												</small>
											</div>
										</div>
										<div className="row mb-2">
											<div className="col-6">
												<small className="text-muted">
													<strong>Paid:</strong> ₹{fmtMoney2(record.paid_amount ?? 0)}
												</small>
											</div>
											<div className="col-6">
												<small className="text-muted">
													<strong>Due:</strong> ₹{fmtMoney2(record.due_amount ?? 0)}
												</small>
											</div>
										</div>
										<div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
											<Link to={`/purchase/${record.id}`}>
												<Button type="primary" size="small">
													View Details
												</Button>
											</Link>
											<Dropdown
												overlay={
													<Menu>
														<Menu.Item
															key="payment"
															icon={<DollarOutlined />}
															onClick={() => navigate(`/payment/supplier/${record.id}`)}
														>
															Payment
														</Menu.Item>
														{onShowPdf && (
															<Menu.Item
																key="preview"
																onClick={() => onShowPdf(record)}
															>
																Preview
															</Menu.Item>
														)}
														{onPrintPdf && (
															<Menu.Item
																key="print"
																onClick={() => onPrintPdf(record)}
															>
																Print PDF
															</Menu.Item>
														)}
														<Menu.Item
															key="edit"
															icon={<EditOutlined />}
															onClick={() => navigate(`/purchase/edit/${record.id}`)}
														>
															Edit
														</Menu.Item>
														<Menu.Item
															key="delete"
															icon={<DeleteOutlined />}
															danger
															onClick={() => handleDelete(record.id)}
														>
															Delete
														</Menu.Item>
													</Menu>
												}
												trigger={["click"]}
												placement="bottomRight"
											>
												<Button type="default" size="small" icon={<MoreOutlined />}>
													Actions
												</Button>
											</Dropdown>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="text-center py-5">
						<p className="text-muted">No purchase invoices found</p>
					</div>
				)}
			</div>
		</div>
	);
}

const GetAllPurch = (props) => {
	const [startdate, setStartdate] = useState(() => {
		const c = getCachedDateFilter(CACHE_KEYS.PURCHASE_LIST);
		return c ? moment(c.startdate) : moment().startOf("month");
	});
	const [enddate, setEnddate] = useState(() => {
		const c = getCachedDateFilter(CACHE_KEYS.PURCHASE_LIST);
		return c ? moment(c.enddate) : moment().endOf("month");
	});

	const dispatch = useDispatch();
	const list = useSelector((state) => state.purchases.list);
	const total = useSelector((state) => state.purchases.total);
	const [status, setStatus] = useState("true");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	// const [total, setTotal] = useState(0);

	const { RangePicker } = DatePicker;

	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	const [pdfModalData, setPdfModalData] = useState(null);
	const [pdfModalAction, setPdfModalAction] = useState("preview"); // "preview" | "print"
	const [pdfModalLoading, setPdfModalLoading] = useState(false);

	const handleShowPdf = async (record) => {
		setPdfModalLoading(true);
		try {
			const response = await axios.get(`purchase-invoice/${record.id}`);
			const data = response.data;
			setPdfModalData(data.singlePurchaseInvoice || record);
			setPdfModalAction("preview");
			setPdfModalOpen(true);
		} catch (err) {
			console.error("Failed to load invoice for PDF:", err);
			setPdfModalData(record);
			setPdfModalAction("preview");
			setPdfModalOpen(true);
		} finally {
			setPdfModalLoading(false);
		}
	};

	const handlePrintPdf = async (record) => {
		setPdfModalLoading(true);
		try {
			const response = await axios.get(`purchase-invoice/${record.id}`);
			const data = response.data;
			setPdfModalData(data.singlePurchaseInvoice || record);
			setPdfModalAction("print");
			setPdfModalOpen(true);
		} catch (err) {
			console.error("Failed to load invoice for PDF:", err);
			setPdfModalData(record);
			setPdfModalAction("print");
			setPdfModalOpen(true);
		} finally {
			setPdfModalLoading(false);
		}
	};

	useEffect(() => {
		dispatch(
			loadAllPurchase({
				status: true,
				page: 1,
				limit: 10,
				startdate: startdate,
				enddate: enddate,
			})
		);
		setCurrentPage(1);
		setPageSize(10);
	}, []);

	//Have to IMPLEMENT TOTAL Purchase info
	// useEffect(() => {
	//   // GetTotalPurchase().then((res) => setTotal(res));
	// }, [list]);

	const onCalendarChange = (dates) => {
		const Newstartdate = dates?.[0] ? (dates[0].format ? dates[0].format("YYYY-MM-DD") : moment(dates[0]).format("YYYY-MM-DD")) : null;
		const Newenddate = dates?.[1] ? (dates[1].format ? dates[1].format("YYYY-MM-DD") : moment(dates[1]).format("YYYY-MM-DD")) : null;
		const startVal = Newstartdate || (startdate?.format ? startdate.format("YYYY-MM-DD") : startdate);
		const endVal = Newenddate || (enddate?.format ? enddate.format("YYYY-MM-DD") : enddate);
		setStartdate(Newstartdate ? moment(Newstartdate) : startdate);
		setEnddate(Newenddate ? moment(Newenddate) : enddate);

		setCachedDateFilter(CACHE_KEYS.PURCHASE_LIST, { startdate: startVal, enddate: endVal });

		dispatch(
			loadAllPurchase({
				status: status,
				page: 1,
				limit: pageSize,
				startdate: startVal,
				enddate: endVal,
			})
		);
		setCurrentPage(1);
	};

	const isLogged = Boolean(localStorage.getItem("isLogged"));

	if (!isLogged) {
		return <Navigate to={"/auth/login"} replace={true} />;
	}
	return (
		<>
			<div className='card card-custom list-page-fill-viewport purchase-list-page'>
				<div className='card-body list-page-card-body'>
					{/* On mobile: same as Sale – header + summary in wrapper (no sticky) */}
					<div className='list-page-sticky-top'>
						<div className='d-flex justify-content-between align-items-center flex-wrap gap-2 list-page-header mb-2' style={{ marginBottom: "8px" }}>
							<h5 className='mb-0'>Purchase Invoice List</h5>
							<div className='d-flex flex-wrap align-items-center gap-2'>
								<RangePicker
									onCalendarChange={onCalendarChange}
									defaultValue={[moment(startdate), moment(enddate)]}
									className='range-picker'
								/>
							</div>
						</div>
						<DashboardCard information={total?._sum} count={total?._count} />
					</div>
					<div className='list-page-table-outer'>
						<CustomTable
							list={list}
							total={total?._count?.id}
							startdate={startdate}
							enddate={enddate}
							status={status}
							setStatus={setStatus}
							onShowPdf={handleShowPdf}
							onPrintPdf={handlePrintPdf}
							paginationConfig={{
								current: currentPage,
								pageSize,
								onChange: (page, size) => {
									setCurrentPage(page);
									setPageSize(size || 10);
								},
							}}
						/>
					</div>
				</div>
			</div>

			{/* PDF modal from kebab: Show PDF / Print PDF */}
			{pdfModalOpen && (
				<>
					{pdfModalLoading && (
						<div className="popUp" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
							<span size="large">Loading...</span>
						</div>
					)}
					{!pdfModalLoading && pdfModalData && (
						<PurchaseInvoice
							data={pdfModalData}
							autoShow={true}
							onClose={() => {
								setPdfModalOpen(false);
								setPdfModalData(null);
							}}
							autoPrintOnce={pdfModalAction === "print"}
						/>
					)}
				</>
			)}
		</>
	);
};

export default GetAllPurch;
